/**
 * Import CAD Tool
 *
 * Imports CAD files (DWG/DXF) by parsing their real geometry (line work,
 * arcs, polylines, text) and placing it as an import element. Opens a real
 * file picker on activation, mirroring BIM's Import CAD dialog flow.
 * The source file is remembered per element so the context-menu Reload can
 * re-parse the current content.
 */

import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { buildCadGeometryData, readCadFile } from './LinkCADTool';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import i18n from '../i18n';

export class ImportCADTool extends BaseTool {
    id = 'import_cad';
    name = 'Import CAD';
    cursor = 'copy';

    private pickedFile: File | null = null;

    onActivate(): void {
        // Dialog-driven command: open the file picker immediately, like
        // BIM's Import CAD dialog. Placement then completes on click.
        void this.pickFile('.dwg,.dxf').then((file) => {
            if (file) {
                this.pickedFile = file;
                this.notify(i18n.t('tools.notify.cadSelected', { fileName: file.name }));
            } else {
                this.exitToSelect();
            }
        });
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        void this.executeImport(this.pickedFile, point);
        this.exitToSelect();
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    public async executeImport(file: File | null, origin?: { x: number; y: number; z: number }) {
        if (!this.context) return;

        const ox = origin?.x ?? 0;
        const oy = origin?.y ?? 0;
        const oz = origin?.z ?? 0;
        const fileName = file?.name ?? 'Imported CAD.dxf';

        // Create the import element first; patch geometry once parsed so the
        // viewport never blocks on large drawings.
        const importElem = {
            id: uuidv4(),
            category: 'Generic Models',
            type: 'Imported CAD',
            parameters: {
                'Imported From': fileName,
                'Layer': '0',
                'Line Count': '0'
            },
            constraints: [],
            geometryData: {
                origin: [ox, oy, oz],
            }
        };

        this.context.bimStore.addElement(importElem as any);

        if (!file) {
            this.notify(i18n.t('tools.notify.importCadNoFileWasSelected'), 'warning');
            return;
        }

        try {
            const result = await readCadFile(file);
            const payload = buildCadGeometryData(result, [ox, oy, oz]);
            this.context.bimStore.updateElement(importElem.id, {
                parameters: {
                    ...importElem.parameters,
                    'Line Count': String(result.lines.length),
                    'Import Units': String(result.insunits),
                },
                geometryData: { ...importElem.geometryData, ...payload },
            } as any);
            // Remember the source so Reload can re-parse without re-picking.
            CadLinkStore.setSource(importElem.id, file, fileName);
            this.notify(i18n.t('tools.notify.cadImported', { fileName: fileName, count: result.lines.length }), 'success');
        } catch (err) {
            this.notify(i18n.t('tools.notify.cadReadFailed', { message: (err as Error).message }), 'error');
        }
    }
}
